<template>
  <el-dialog
      :model-value="modelValue"
      :title="isEdit ? 'Ustunni tahrirlash' : 'Yangi ustun'"
      width="640px"
      destroy-on-close
      @update:model-value="$emit('update:modelValue', $event)"
  >
    <div v-loading="saving" class="sbs-col-form">
      <!-- Title -->
      <div class="sbs-field">
        <label class="sbs-field__label">Sarlavha <span class="sbs-req">*</span></label>
        <el-tabs v-model="titleTab" class="sbs-lang-tabs">
          <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
            <template #label>
              <span class="sbs-lang-tab-label">
                {{ lt.tabLabel }}
                <span class="sbs-lang-dot" :class="{'is-filled': !!form.title[lt.code]}"/>
              </span>
            </template>
            <el-input v-model="form.title[lt.code]" placeholder="Masalan: Summa"/>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- Group -->
      <div class="sbs-field">
        <label class="sbs-field__label">Guruh</label>
        <el-select
            v-model="groupSelectValue"
            filterable
            class="sbs-w-full"
            @change="onGroupSelect"
        >
          <el-option label="Guruhsiz" value=""/>
          <el-option
              v-for="g in groupOptions"
              :key="g.group_code"
              :label="`${displayLangText(g.group) || g.group_code} (${g.group_code})`"
              :value="g.group_code"
          />
          <el-option label="+ Yangi guruh yaratish" :value="NEW_GROUP_VALUE"/>
        </el-select>
      </div>

      <template v-if="creatingNewGroup">
        <div class="sbs-field">
          <label class="sbs-field__label">Yangi guruh nomi <span class="sbs-req">*</span></label>
          <el-tabs v-model="groupTab" class="sbs-lang-tabs">
            <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
              <template #label>
                <span class="sbs-lang-tab-label">
                  {{ lt.tabLabel }}
                  <span class="sbs-lang-dot" :class="{'is-filled': !!form.group[lt.code]}"/>
                </span>
              </template>
              <el-input v-model="form.group[lt.code]" placeholder="Masalan: Tasdiqlangan"/>
            </el-tab-pane>
          </el-tabs>
        </div>
        <div class="sbs-field">
          <label class="sbs-field__label">
            Guruh kodi
            <span class="sbs-hint">— nomdan avtomatik yaratiladi, kerak bo'lsa o'zgartiring</span>
          </label>
          <el-input v-model="form.group_code" placeholder="masalan: tasdiqlangan" @input="groupCodeEdited = true"/>
        </div>
      </template>
      <div v-else-if="form.group_code" class="sbs-field">
        <label class="sbs-field__label">Guruh nomi</label>
        <el-tabs v-model="groupTab" class="sbs-lang-tabs">
          <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
            <template #label>
              <span class="sbs-lang-tab-label">
                {{ lt.tabLabel }}
                <span class="sbs-lang-dot" :class="{'is-filled': !!form.group[lt.code]}"/>
              </span>
            </template>
            <el-input v-model="form.group[lt.code]" placeholder="Masalan: Tasdiqlangan"/>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- Unit -->
      <div class="sbs-field">
        <label class="sbs-field__label">
          Birlik matni
          <span class="sbs-hint">— bo'sh qoldiring, tanlangan birlikdan avtomatik yoziladi</span>
        </label>
        <el-tabs v-model="unitTab" class="sbs-lang-tabs">
          <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
            <template #label>
              <span class="sbs-lang-tab-label">
                {{ lt.tabLabel }}
                <span class="sbs-lang-dot" :class="{'is-filled': !!form.unit[lt.code]}"/>
              </span>
            </template>
            <el-input v-model="form.unit[lt.code]" placeholder="Nostandart birlik uchun (masalan: dona)"/>
          </el-tab-pane>
        </el-tabs>
      </div>

      <el-row :gutter="16">
        <el-col :span="12">
          <div class="sbs-field">
            <label class="sbs-field__label">Turi</label>
            <el-select v-model="form.value_type" class="sbs-w-full" @change="onValueTypeChange">
              <el-option v-for="t in VALUE_TYPES" :key="t.value" :label="t.label" :value="t.value"/>
            </el-select>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="sbs-field">
            <label class="sbs-field__label">Tartib</label>
            <el-input-number v-model="form.order" :min="0" :step="10" class="sbs-w-full" @change="orderTouched = true"/>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="16" v-if="form.value_type !== 'TEXT'">
        <el-col :span="12">
          <div class="sbs-field">
            <label class="sbs-field__label">Birlik (miqyos)</label>
            <el-select
                v-model="form.scale"
                class="sbs-w-full"
                :disabled="form.value_type === 'PERCENT'"
            >
              <el-option v-for="o in SCALE_UNIT_OPTIONS" :key="o.value" :label="o.label" :value="o.value"/>
            </el-select>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="sbs-field">
            <label class="sbs-field__label">Kasr xonalar</label>
            <el-input-number v-model="form.decimals" :min="0" :max="6" class="sbs-w-full"/>
          </div>
        </el-col>
      </el-row>

      <div v-if="form.value_type === 'AMOUNT'" class="sbs-preview">
        Namuna: <b>447541500000000</b> so'm →
        <b>{{ previewText }}</b>
      </div>

      <el-row :gutter="16">
        <el-col :span="12">
          <div class="sbs-field">
            <label class="sbs-field__label">Qaysi yilga biriktirilsin</label>
            <div class="sbs-year-row">
              <el-select v-model="yearEnabled" style="width: 190px" @change="onYearModeChange">
                <el-option :label="`Faqat ${defaultYear}-yil`" :value="true"/>
                <el-option label="Hamma yillar" :value="false"/>
              </el-select>
              <el-input-number
                  v-if="yearEnabled"
                  v-model="form.year"
                  :min="1900"
                  :max="2200"
                  controls-position="right"
                  style="width: 130px"
              />
            </div>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="sbs-field">
            <label class="sbs-field__label">Holati</label>
            <el-switch
                v-model="form.is_active"
                active-text="Faol"
                inactive-text="Nofaol"
                style="--el-switch-on-color: #16A34A"
            />
          </div>
        </el-col>
      </el-row>

      <div class="sbs-year-warn" :class="{ 'is-info': yearEnabled }">
        <template v-if="yearEnabled">
          Bu ustun faqat <b>{{ form.year }}</b>-yil jadvalida ko'rinadi. Boshqa yilga kerak bo'lsa —
          "Yangi yil" oynasidagi <b>"Ustunlar skeletini ko'chirish"</b> orqali nusxalanadi.
        </template>
        <template v-else>
          ⚠ "Hamma yillar" ustuni <b>har bir yil</b> jadvalida ko'rinadi va uni bitta yildan
          o'chirib bo'lmaydi. Faqat har yili aynan takrorlanadigan ustunlar uchun tanlang.
        </template>
      </div>
    </div>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">Bekor qilish</el-button>
      <el-button type="primary" :loading="saving" @click="onSave">Saqlash</el-button>
    </template>
  </el-dialog>
</template>

<script>
import {ElMessage, ElNotification} from "element-plus";
import {
  API_BASE, LANG_TABS, VALUE_TYPES, SCALE_UNIT_OPTIONS,
  emptyMultiLang, fillMultiLang, multiLangOrNull, displayLangText,
  formatScaled, slugifyGroupCode,
} from "./subsidiesTypes";

const NEW_GROUP_VALUE = "__new__";

export default {
  name: "SubsidiesColumnDialog",
  props: {
    modelValue: {type: Boolean, default: false},
    column: {type: Object, default: null},
    columns: {type: Array, default: () => []},
    groupOptions: {type: Array, default: () => []},
    // Jadval yili — yangi ustun sukut bo'yicha shu yilga biriktiriladi.
    defaultYear: {type: Number, required: true},
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      NEW_GROUP_VALUE,
      langTabs: LANG_TABS,
      VALUE_TYPES,
      SCALE_UNIT_OPTIONS,
      titleTab: "uz_lat",
      groupTab: "uz_lat",
      unitTab: "uz_lat",
      yearEnabled: false,
      saving: false,
      groupSelectValue: "",
      creatingNewGroup: false,
      groupCodeEdited: false,
      orderTouched: false,
      form: this.buildForm(null),
    };
  },
  computed: {
    isEdit() {
      return !!this.column?.id;
    },
    previewText() {
      return formatScaled(447541500000000, this.form.scale, this.form.decimals) + " " +
          (this.SCALE_UNIT_OPTIONS.find((o) => o.value === this.form.scale)?.label || "");
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.form = this.buildForm(this.column);
        this.yearEnabled = this.form.year !== null && this.form.year !== undefined;
        this.titleTab = "uz_lat";
        this.groupTab = "uz_lat";
        this.unitTab = "uz_lat";
        this.creatingNewGroup = false;
        this.groupCodeEdited = false;
        this.groupSelectValue = this.form.group_code || "";
        this.orderTouched = false;
        if (!this.isEdit) this.form.order = this.computeNextOrder(this.form.group_code);
      }
    },
    "form.group.uz_lat"(val) {
      if (this.creatingNewGroup && !this.groupCodeEdited) {
        this.form.group_code = slugifyGroupCode(val);
      }
    },
  },
  methods: {
    displayLangText,
    buildForm(column) {
      const isNew = !column?.id;
      return {
        group_code: column?.group_code || "",
        group: fillMultiLang(column?.group),
        title: fillMultiLang(column?.title),
        unit: fillMultiLang(column?.unit),
        value_type: column?.value_type || "AMOUNT",
        scale: column?.scale ?? 9,
        decimals: column?.decimals ?? 3,
        year: column?.year ?? (isNew ? this.defaultYear : null),
        order: column?.order ?? 10,
        is_active: column?.is_active !== false,
      };
    },
    // CONSOLE.md §7.4 — new columns should slot right after their group (or append
    // to the very end when ungrouped/new-group) instead of always defaulting to 10.
    computeNextOrder(groupCode) {
      const relevant = this.columns.filter((c) => c.id !== this.column?.id);
      if (groupCode && groupCode !== NEW_GROUP_VALUE) {
        const inGroup = relevant.filter((c) => c.group_code === groupCode);
        if (inGroup.length) return Math.max(...inGroup.map((c) => c.order)) + 1;
      }
      if (!relevant.length) return 10;
      return Math.max(...relevant.map((c) => c.order)) + 10;
    },
    onGroupSelect(value) {
      if (value === NEW_GROUP_VALUE) {
        this.creatingNewGroup = true;
        this.groupCodeEdited = false;
        this.form.group_code = "";
        this.form.group = emptyMultiLang();
      } else {
        this.creatingNewGroup = false;
        this.form.group_code = value;
        const existing = this.groupOptions.find((g) => g.group_code === value);
        this.form.group = fillMultiLang(existing?.group);
      }
      if (!this.isEdit && !this.orderTouched) {
        this.form.order = this.computeNextOrder(this.form.group_code || value);
      }
    },
    // Yilga biriktirish rejimiga qaytilganda bo'sh input qolmasin.
    onYearModeChange(enabled) {
      if (enabled && !this.form.year) this.form.year = this.defaultYear;
    },
    onValueTypeChange(type) {
      if (type === "PERCENT") {
        this.form.scale = 0;
        this.form.decimals = 1;
      } else if (type === "TEXT") {
        this.form.scale = 0;
        this.form.decimals = 0;
      } else if (type === "AMOUNT" && !this.form.scale) {
        this.form.scale = 9;
        this.form.decimals = 3;
      }
    },
    validate() {
      const missing = this.langTabs.filter((lt) => !this.form.title[lt.code]?.trim());
      if (missing.length) {
        ElMessage({type: "warning", message: "Sarlavhani barcha 5 tilda to'ldiring"});
        this.titleTab = missing[0].code;
        return false;
      }
      if (this.creatingNewGroup && !this.form.group_code.trim()) {
        ElMessage({type: "warning", message: "Yangi guruh nomini kiriting (yoki guruh kodini qo'lda yozing)"});
        this.groupTab = "uz_lat";
        return false;
      }
      return true;
    },
    onSave() {
      if (!this.validate()) return;
      const payload = {
        group_code: this.form.group_code || "",
        group: this.form.group_code ? multiLangOrNull(this.form.group) : null,
        title: this.form.title,
        unit: multiLangOrNull(this.form.unit),
        value_type: this.form.value_type,
        scale: this.form.value_type === "TEXT" ? 0 : this.form.scale,
        decimals: this.form.decimals,
        year: this.yearEnabled ? this.form.year : null,
        order: this.form.order,
        is_active: this.form.is_active,
      };
      this.saving = true;
      const request = this.isEdit
          ? this.$http.put(`${API_BASE}/columns/${this.column.id}`, payload)
          : this.$http.post(`${API_BASE}/columns`, payload);
      request
          .then(() => {
            ElMessage({type: "success", message: this.isEdit ? "Ustun yangilandi" : "Ustun yaratildi"});
            this.$emit("saved");
            this.$emit("update:modelValue", false);
          })
          .catch((error) => {
            ElNotification({
              title: "Saqlashda xatolik",
              message: error?.response?.data?.message || "Iltimos, qayta urinib ko'ring.",
              type: "error",
            });
          })
          .finally(() => {
            this.saving = false;
          });
    },
  },
};
</script>

<style scoped lang="scss">
.sbs-col-form {
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 4px;
}

.sbs-field {
  margin-bottom: 16px;

  &__label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #303133;
  }
}

.sbs-req {
  color: #e11d48;
}

.sbs-hint {
  font-weight: 400;
  font-size: 12px;
  color: #8a94a6;
}

.sbs-w-full {
  width: 100%;
}

.sbs-lang-tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 8px;
  }

  :deep(.el-tabs__nav-wrap::after) {
    height: 1px;
  }
}

.sbs-lang-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.sbs-lang-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d0d5dd;

  &.is-filled {
    background: #16a34a;
  }
}

.sbs-year-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sbs-year-warn {
  margin: -6px 0 16px;
  padding: 8px 12px;
  background: rgba(234, 179, 8, 0.12);
  border: 1px solid rgba(234, 179, 8, 0.35);
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #92620a;

  &.is-info {
    background: rgba(0, 119, 145, 0.07);
    border-color: rgba(0, 119, 145, 0.22);
    color: #005c72;
  }
}

.sbs-preview {
  margin: -6px 0 16px;
  padding: 8px 12px;
  background: rgba(0, 119, 145, 0.06);
  border-radius: 8px;
  font-size: 12.5px;
  color: #005c72;
}
</style>
